import React, { useEffect } from "react";
import { removeUser } from "./Redux/Actions/UserActions";
import { getAuthTokens } from "./utils/cookieUtils";
import { useDispatch, useSelector } from "react-redux";
import { useWebSocket } from "./hooks/useWebSocket";

import TopNavigation from "./component/NavComponents/TopNavigation";
import PublicNavBar from "./component/NavComponents/PublicNavBar";
import SignIn from "./Pages/AuthPages/SignIn";
import SignUp from "./Pages/AuthPages/SignUp";
import AuthorizedRoute from "./component/AuthorizedRoute";
import Portfolio from "./Pages/Portfolio/Portfolio";
import PortfolioV2 from "./Pages/PortfolioV2";
import BrokerPage from "./Pages/Broker";
import Funds from "./Pages/Funds";
import Strategies from "./Pages/Strategies";
import Custodian from "./Pages/Custodian";
import TradeBlotter from "./Pages/TradeBlotter";
import PerformanceDashboard from "./Pages/PerformanceDashboard/PerformanceDashboard";
import PerformanceDashboardV2 from "./Pages/PerformanceDashboardV2";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { selectIsStrategist } from "./Redux/Reducers/selectors";
import FundAllocator from "./Pages/FundAllocator/FundAllocator";
import Signals from "./Pages/Signals";
import Rebalancer from "./Pages/Rebalancer/Rebalancer";
import Accounting from "./Pages/Accounting";
import ForgotUserPassword from "./Pages/AuthPages/ForgotUserPassword";
import ResetUserPassword from "./Pages/AuthPages/ResetUserPassword";
import ResetPasswordOTP from "./Pages/AuthPages/ResetPasswordOTP";
import SecurityMaster from "./Pages/SecurityMaster";
import AddeparInventory from "./Pages/AddeparInventory";
import ReportsPage from "./Pages/Reports";
import HistoricalReports from "./Pages/Reports/HistoricalReports";
import Positions from "./Pages/Positions";
import FundsBlacklist from "./Pages/FundsBlacklist";
import BoxedPositionsTable from "./Pages/BoxedPositions";
import Store from "./Pages/Store";
import StrategyDetails from "./Pages/Store/Strategy/index";
import StrategyComparison from "./Pages/Store/Strategy/Comparison/index";
import Strategist from "./Pages/Strategist/index"
import StrategyPerformance from "./Pages/StrategyPerformance";
import ManagerOverview from "./Pages/ManagerOverview/ManagerOverviewDashboard";
import AddStrategyPage from "./Pages/Strategies/Add/index";
import StrategyPermissionsPage from "./Pages/Strategies/Permissions/index";
import Settings from "./Pages/Settings/index";
import AdminTools from "./Pages/AdminTools/index";

// used for public store routes accessible without login.
const PublicStoreLayout = ({ children }) => (
  <div className="app">
    <PublicNavBar />
    <main className="content">{children}</main>
  </div>
);

// Define a configuration array for routesf
const routesConfig = [
  { path: '/portfolio', component: Portfolio },
  { path: '/portfolio/:fundName', component: Portfolio },
  { path: '/portfolio/:fundName/:strategyName', component: Portfolio },
  { path: '/portfolio-v2', component: PortfolioV2 },
  { path: '/portfolio-v2/:fundName', component: PortfolioV2 },
  { path: '/portfolio-v2/:fundName/:strategyName', component: PortfolioV2 },
  { path: '/funds', component: Funds },
  { path: '/strategies', component: Strategies },
  { path: '/strategies/add', component: AddStrategyPage },
  { path: '/strategies/permissions', component: StrategyPermissionsPage },
  { path: '/broker', component: BrokerPage },
  { path: '/custodian', component: Custodian },
  { path: '/security', component: SecurityMaster },
  { path: '/addepar-inventory', component: AddeparInventory },
  { path: '/funds-blacklist', component: FundsBlacklist },
  { path: '/reports', component: ReportsPage },
  { path: '/historical-reports', component: HistoricalReports },
  { path: '/positions', component: Positions },
  { path: '/boxed-positions', component: BoxedPositionsTable },
  { path: '/tradesV2', component: TradeBlotter },
  { path: '/fundAllocator', component: FundAllocator },
  { path: '/signals', component: Signals },
  { path: '/rebalancer', component: Rebalancer },
  { path: '/accounting', component: Accounting },
  { path: '/vantage', component: PerformanceDashboard },
  { path: '/vantage-v2', component: PerformanceDashboardV2 },
  { path: '/manager-overview', component: ManagerOverview },
  { path: '/store', component: Store },
  { path: '/store/strategy/:ownerSlug/:slug', component: StrategyDetails },
  { path: '/store/strategy/:ownerSlug/:strategySlug/compare/:compareSlugs', component: StrategyComparison },
  { path: '/strategist/:strategistId', component: Strategist },
  { path: '/strategy-performance', component: StrategyPerformance },
  { path: '/settings', component: Settings },
  { path: '/settings/:section', component: Settings },
  { path: '/settings/:section/:item', component: Settings },
  { path: '/admin-tools', component: AdminTools },

];

// Helper function to render routes
const renderRoutes = (routes) => {
  
  return routes.map(({ path, component: Component }) => (
    <Route
      key={path}
      path={path}
      element={
        <AuthorizedRoute>
          <Component />
        </AuthorizedRoute>
      }
    />
  ));
};

function MainRoutes(props) {
  
  const hasAuth = props.token; // In-memory auth token (user session state)
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { disconnect } = useWebSocket();
  const isStrategist = useSelector(selectIsStrategist);

  const managers = props.managers;
  const hasManagers = Array.isArray(managers) && managers.length > 0;
  const defaultAppPath = isStrategist
    ? "/portfolio"
    : hasManagers && managers.length > 1
      ? "/manager-overview"
      : "/vantage";

  useEffect(() => {
    const tokens = getAuthTokens(); // Check for the token in cookies
    // If session exists in memory but not in storage, force logout
    if (!tokens && hasAuth) {
      dispatch(removeUser());
      disconnect();
      navigate("/login", { replace: true });
    }
  }, [location.pathname]);
  
  return (
    <>
      {!hasAuth ? (
        <>
          <Routes>
            <Route path="/" element={<SignIn />} />
            <Route path="/login" element={<SignIn />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/forgot" element={<ForgotUserPassword />} />
            <Route path="/resetPass" element={<ResetUserPassword />} />
            <Route path="/reset-otp" element={<ResetPasswordOTP />} />
            <Route path="/store/strategy/:ownerSlug/:slug" element={<PublicStoreLayout><StrategyDetails /></PublicStoreLayout>} />
            <Route path="/store/strategy/:ownerSlug/:strategySlug/compare/:compareSlugs" element={<PublicStoreLayout><StrategyComparison /></PublicStoreLayout>} />
            <Route path="/store" element={<PublicStoreLayout><Store /></PublicStoreLayout>} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </>
      ) : (
        <>
          <div className="app">
            <TopNavigation />
            <main className="content">
              {hasManagers ? (
                <>
                  <Routes>
                    {renderRoutes(routesConfig)}
                    <Route path="*" element={<Navigate to={defaultAppPath} replace />} />
                  </Routes>
                </>
              ) : (
                <Routes>
                  {renderRoutes(routesConfig.filter(route => ['/portfolio', '/signals', '/strategies', '/strategies/add', '/strategies/permissions', '/strategy-performance', '/settings', '/settings/:section', '/settings/:section/:item', '/store', '/store/strategy/:ownerSlug/:slug', '/store/strategy/:ownerSlug/:strategySlug/compare/:compareSlugs', '/admin-tools'].includes(route.path)))}
                  <Route path="*" element={<Navigate to="/portfolio" replace />} />
                </Routes>
              )}
            </main>
          </div>
        </>
      )}
    </>
  );
}

export default MainRoutes;
